import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  activarUbicacionHttp,
  createUbicacionHttp,
  desactivarUbicacionHttp,
  editarUbicacionHttp,
  getUbicacionesHttp,
} from "actions/ubicacion";
import { getAlmacenesHttp } from "actions/almacen";

export const useUbicacion = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [procesando, setProcesando] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  /* CREAR CATEGORÍA */
  const [procesandoUbicacion, setProcesandoUbicacion] = useState<boolean>(false);
  const [isAlertOpenCrearUbicacion, setIsAlertOpenCrearUbicacion] = useState(false);
  const handleisAlertCrearUbicacionOpen = () => setIsAlertOpenCrearUbicacion(true);
  const handleisAlertCrearUbicacionClose = () => setIsAlertOpenCrearUbicacion(false);

  const [verEditarUbicacion, setVerEditarUbicacion] = useState<any>(null);
  const [isAlertOpenEditarUbicacion, setIsAlertOpenEditarUbicacion] = useState(false);
  const handleisAlertOpenEditarUbicacion = () => setIsAlertOpenEditarUbicacion(true);
  const handleisAlertCloseEditarUbicacion = () => setIsAlertOpenEditarUbicacion(false);

  const [ubicaciones, setUbicaciones] = useState<any[]>([]);
  const [almacenes, setAlmacenes] = useState<any[]>([]);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //para buscador
  const [buscador, setBuscador] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  //Debounce para evitar muchas peticiones
  const handleBuscadorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBuscador(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      getUbicaciones({ search: value });
    }, 500);
    setDebounceTimeout(timeout);
  };

  const formik = useFormik({
    initialValues: {
      clave: "",
      nombre: "",
      descripcion: "",
      id_almacen: "",
    },
    validationSchema: Yup.object({
      clave: Yup.string(),
      nombre: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      descripcion: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      id_almacen: Yup.number().required(intl.formatMessage({ id: "input_validation_requerido" })),
    }),
    onSubmit: async (values) => {
      //console.log("Formulario enviado:", values);
    },
  });

  const isFieldValid = (fieldName: keyof typeof formik.values) => {
    return (
      formik.touched[fieldName] &&
      !formik.errors[fieldName] &&
      formik.values[fieldName] &&
      formik.values[fieldName] !== ""
    );
  };

  const getFieldColor = (fieldName: keyof typeof formik.values) => {
    return isFieldValid(fieldName) ? "#00AB16" : undefined;
  };

  const getAlmacenes = useCallback(async () => {
    try {
      setProcesando(true);
      const unidadesMedida = await getAlmacenesHttp();
      setAlmacenes(unidadesMedida);
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
      handleisAlertOpen();
    }
  }, []);

  const getUbicaciones = useCallback(
    async (params?: { search?: string; fecha1?: string; fecha2?: string }) => {
      try {
        setProcesando(true);
        const unidadesMedida = await getUbicacionesHttp(params?.search);
        setUbicaciones(unidadesMedida);
        setProcesando(false);
      } catch (error) {
        setProcesando(false);
        const message = getErrorHttpMessage(error);
        setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
        handleisAlertOpen();
      }
    },
    []
  );

  const createUbicacion = async (datos: any) => {
    try {
      setProcesandoUbicacion(true);
      const response: any = await createUbicacionHttp(datos);

      const almacenSeleccionado = almacenes?.find(
        (a: any) => a.id === datos.id_almacen || a.id === Number(datos.id_almacen)
      );

      const nuevaUbicacion = {
        id_ubicacion: response?.id,
        id_almacen: response?.id_almacen,
        nombre_almacen: almacenSeleccionado?.nombre,
        clave: response?.clave,
        nombre: response?.nombre,
        descripcion: response?.descripcion,
        estatus: response?.estatus,
      };

      setUbicaciones((prevUbicaciones: any[]) => [nuevaUbicacion, ...prevUbicaciones]);

      setMensajeAlert(intl.formatMessage({ id: "general_añadido_correctamente" }));
      formik.resetForm();
      handleisAlertCrearUbicacionClose();
      handleisAlertOpen();
      setProcesandoUbicacion(false);
    } catch (error) {
      setProcesandoUbicacion(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "error_añadir_general" }));
      handleisAlertOpen();
    }
  };
  const editarUbicacion = async (datos: any) => {
    try {
      setProcesandoUbicacion(true);
      const response: any = await editarUbicacionHttp(datos);

      setUbicaciones((prevUbicaciones: any[]) =>
        prevUbicaciones.map((c) =>
          c.id_ubicacion === response?.id
            ? {
                ...c,
                clave: response?.clave,
                nombre: response?.nombre,
                descripcion: response?.descripcion,
              }
            : c
        )
      );
      setMensajeAlert(intl.formatMessage({ id: "general_editado_correctamente" }));
      formik.resetForm();
      handleisAlertCloseEditarUbicacion();
      handleisAlertOpen();
      setProcesandoUbicacion(false);
    } catch (error) {
      setProcesandoUbicacion(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_editado_error" }));
      handleisAlertOpen();
    }
  };

  const desactivarUbicacion = async (datos: any) => {
    try {
      setProcesando(true);
      const ubicacion: any = await desactivarUbicacionHttp(datos);
      setUbicaciones((prevTipoProductos: any[]) => {
        return prevTipoProductos.map((c) => {
          if (c.id_ubicacion === ubicacion.id) {
            return {
              ...c,
              estatus: ubicacion.estatus,
            };
          }
          return c;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_desactivado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_desactivado_error" }));
      handleisAlertOpen();
    }
  };
  const activarUbicacion = async (datos: any) => {
    try {
      setProcesando(true);
      const ubicacion: any = await activarUbicacionHttp(datos);
      setUbicaciones((prevUbicaciones: any[]) => {
        return prevUbicaciones.map((t) => {
          if (t.id_ubicacion === ubicacion.id) {
            return {
              ...t,
              estatus: ubicacion.estatus,
            };
          }
          return t;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_activado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_activado_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (verEditarUbicacion) {
      formik.setValues({
        clave: verEditarUbicacion?.clave || "",
        nombre: verEditarUbicacion?.nombre || "",
        descripcion: verEditarUbicacion?.descripcion || "",
        id_almacen: verEditarUbicacion?.id_almacen || 0,
      });
    }
  }, [verEditarUbicacion]);

  useEffect(() => {
    getAlmacenes();
    getUbicaciones();
  }, []);

  // Handlers para la paginación
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const ubicacionesPaginadas = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return ubicaciones?.slice(startIndex, endIndex) || [];
  }, [ubicaciones, page, rowsPerPage]);

  return {
    intl,
    formik,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    ubicaciones,
    almacenes,
    //crear categoria
    isAlertOpenCrearUbicacion,
    handleisAlertCrearUbicacionOpen,
    handleisAlertCrearUbicacionClose,
    getFieldColor,
    procesandoUbicacion,
    createUbicacion,
    desactivarUbicacion,
    activarUbicacion,
    editarUbicacion,
    verEditarUbicacion,
    isAlertOpenEditarUbicacion,
    handleisAlertOpenEditarUbicacion,
    handleisAlertCloseEditarUbicacion,
    setVerEditarUbicacion,
    ubicacionesPaginadas,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    buscador,
    handleBuscadorChange,
  };
};
